import { ComponentType } from "../services/types/ComponentTypes";
import { toUTC } from "./dateUtils";

export type ComputeDueDateConfig = {
  purchaseDateField: string;
  creditDaysField: string;
};

function parseLocalDate(value: unknown): Date | null {
  if (value === undefined || value === null || value === "") return null;
  if (value instanceof Date && !isNaN(value.getTime())) {
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  const str = String(value).trim();
  if (!str) return null;

  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const d = new Date(`${str.slice(0, 10)}T00:00:00`);
    return isNaN(d.getTime()) ? null : d;
  }

  const ddmm = str.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
  if (ddmm) {
    const d = new Date(+ddmm[3], +ddmm[2] - 1, +ddmm[1]);
    return isNaN(d.getTime()) ? null : d;
  }

  const slash = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (slash) {
    const d = new Date(+slash[3], +slash[2] - 1, +slash[1]);
    return isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(str);
  if (isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

export function computeDueDateFromCreditDays(
  purchaseDate: unknown,
  creditDays: unknown
): string | null {
  const base = parseLocalDate(purchaseDate);
  if (!base) return null;

  const daysRaw =
    creditDays === undefined || creditDays === null || creditDays === ""
      ? 0
      : parseInt(String(creditDays), 10);
  const days = Number.isNaN(daysRaw) ? 0 : Math.max(0, daysRaw);

  const due = new Date(base);
  due.setDate(due.getDate() + days);
  return toUTC(due);
}

function walkComponents(comps: any[], visitor: (comp: any) => void): void {
  for (const comp of comps) {
    visitor(comp);
    if (comp.Fields && Array.isArray(comp.Fields)) {
      walkComponents(comp.Fields, visitor);
    }
    if (comp.childComponents && Array.isArray(comp.childComponents)) {
      walkComponents(comp.childComponents, visitor);
    }
    if (comp.childControls && Array.isArray(comp.childControls)) {
      walkComponents(comp.childControls, visitor);
    }
  }
}

export function findDueDateComputeFields(config: any[]): Array<{
  dueFieldName: string;
  jsonAttribute?: string;
  compute: ComputeDueDateConfig;
}> {
  const fields: Array<{
    dueFieldName: string;
    jsonAttribute?: string;
    compute: ComputeDueDateConfig;
  }> = [];

  walkComponents(config, (comp) => {
    if (
      comp.componentType !== ComponentType.DATE_FIELD &&
      comp.componentType !== "dateField"
    ) {
      return;
    }
    const compute = comp.ComputeDueDate as ComputeDueDateConfig | undefined;
    if (!compute?.purchaseDateField || !compute?.creditDaysField) return;
    const dueFieldName = comp.name || comp.BindValue;
    if (!dueFieldName) return;
    fields.push({
      dueFieldName,
      jsonAttribute: comp.Json_Attribute,
      compute,
    });
  });

  return fields;
}

export function applyDueDateComputeFields(
  config: any[],
  formData: Record<string, any>,
  onChange: (name: string, value: string) => void
): void {
  for (const field of findDueDateComputeFields(config)) {
    const purchaseDate = formData[field.compute.purchaseDateField];
    const creditDays = formData[field.compute.creditDaysField];
    const computed = computeDueDateFromCreditDays(purchaseDate, creditDays);
    if (!computed) continue;

    const current =
      formData[field.dueFieldName] ??
      (field.jsonAttribute ? formData[field.jsonAttribute] : undefined);
    if (current !== undefined && String(current) === String(computed)) {
      continue;
    }

    onChange(field.dueFieldName, computed);
    if (field.jsonAttribute && field.jsonAttribute !== field.dueFieldName) {
      onChange(field.jsonAttribute, computed);
    }
  }
}
